/**
 * SKOP Backend API Client
 *
 * Frontend client for communicating with the Vercel API backend.
 */

import type {
  AuthChallengeResponse,
  AuthVerifyResponse,
  ValidateSessionResponse,
  VerifyPaymentResponse,
  CreateApiKeyResponse,
  ListApiKeysResponse,
  SessionUser,
} from './types';

export type { SessionUser };

// API base URL (use relative path for same-origin, or env var for different origin)
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

// Session storage key
const TOKEN_KEY = 'skop_session_token';

// ============================================================================
// Token Management
// ============================================================================

/**
 * Get stored session token
 */
export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Store session token
 */
export function storeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    console.error('Failed to store token');
  }
}

/**
 * Clear session token
 */
export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    console.error('Failed to clear token');
  }
}

// ============================================================================
// HTTP Client
// ============================================================================

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  authenticated?: boolean;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, authenticated = true } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Include token in header for API clients (cookie is sent automatically for browser)
  if (authenticated) {
    const token = getStoredToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    // Include credentials for cookie-based auth (more secure than localStorage)
    credentials: 'include',
  });

  const data = await response.json();

  if (!response.ok) {
    throw new ApiClientError(
      data.error || 'Request failed',
      response.status,
      data.code
    );
  }

  return data as T;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// ============================================================================
// Authentication
// ============================================================================

/**
 * Request authentication challenge
 */
export async function requestAuthChallenge(
  walletAddress: string
): Promise<AuthChallengeResponse> {
  return request<AuthChallengeResponse>('/api/auth/challenge', {
    method: 'POST',
    body: { walletAddress },
    authenticated: false,
  });
}

/**
 * Verify signature and get session token
 */
export async function verifySignature(
  walletAddress: string,
  signature: string,
  nonce: string
): Promise<AuthVerifyResponse> {
  const response = await request<AuthVerifyResponse>('/api/auth/verify', {
    method: 'POST',
    body: { walletAddress, signature, nonce },
    authenticated: false,
  });

  // Store the token
  storeToken(response.token);

  return response;
}

/**
 * Validate current session
 * Checks both localStorage token AND HttpOnly cookie (cookie sent automatically)
 */
export async function validateSession(): Promise<ValidateSessionResponse> {
  try {
    // Always make the request - cookie will be sent automatically via credentials: 'include'
    // localStorage token is sent in Authorization header as backup for API clients
    return await request<ValidateSessionResponse>('/api/auth/session', {
      method: 'GET',
      authenticated: true,
    });
  } catch (error) {
    // Clear invalid token from localStorage
    if (error instanceof ApiClientError && error.statusCode === 401) {
      clearToken();
    }
    return { valid: false };
  }
}

/**
 * Logout (invalidate session)
 */
export async function logout(): Promise<void> {
  try {
    await request('/api/auth/logout', {
      method: 'POST',
      authenticated: true,
    });
  } finally {
    clearToken();
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getStoredToken() !== null;
}

// ============================================================================
// Payments
// ============================================================================

/**
 * Verify payment transaction
 */
export async function verifyPayment(
  signature: string,
  expectedAmountSol: number
): Promise<VerifyPaymentResponse> {
  return request<VerifyPaymentResponse>('/api/payments/verify', {
    method: 'POST',
    body: { signature, expectedAmountSol },
    authenticated: true,
  });
}

// ============================================================================
// API Keys
// ============================================================================

/**
 * List user's API keys
 */
export async function listApiKeys(): Promise<ListApiKeysResponse> {
  return request<ListApiKeysResponse>('/api/keys', {
    method: 'GET',
    authenticated: true,
  });
}

/**
 * Create a new API key
 */
export async function createApiKey(label: string): Promise<CreateApiKeyResponse> {
  return request<CreateApiKeyResponse>('/api/keys', {
    method: 'POST',
    body: { label },
    authenticated: true,
  });
}

/**
 * Revoke an API key
 */
export async function revokeApiKey(keyId: string): Promise<{ success: boolean }> {
  return request(`/api/keys/${keyId}`, {
    method: 'PATCH',
    body: { revoke: true },
    authenticated: true,
  });
}

/**
 * Delete an API key
 */
export async function deleteApiKey(keyId: string): Promise<{ success: boolean }> {
  return request(`/api/keys/${keyId}`, {
    method: 'DELETE',
    authenticated: true,
  });
}

/**
 * Update API key label
 */
export async function updateApiKeyLabel(
  keyId: string,
  label: string
): Promise<{ success: boolean }> {
  return request(`/api/keys/${keyId}`, {
    method: 'PATCH',
    body: { label },
    authenticated: true,
  });
}

// ============================================================================
// User Data
// ============================================================================

/**
 * Get current user info
 * Convenience wrapper around validateSession
 */
export async function getCurrentUser(): Promise<SessionUser | null> {
  const session = await validateSession();
  return session.valid && session.user ? session.user : null;
}
